import moment, { Moment } from "moment-timezone";
import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import IActiveLoginMessage from "../models/IActiveLoginMessage";
import ILogin_Message_Detail from "../models/ILogin_Message_Detail";
import IAcknowledgeLoginMessageDTO from "../models/DTO/IAcknowledgeLoginMessageDTO";
import IOnAcknowledgeLoginMessageDTO from "../models/DTO/IOnAcknowledgeLoginMessageDTO";
import IOnGetActiveLoginMessageDTO from "../models/DTO/IOnGetActiveLoginMessageDTO";

export const LOGIN_MESSAGE_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

/** The server answered 409: the message changed under the user. Re-fetch and re-prompt with the current one. */
export class LoginMessageSupersededError extends Error {
    currentGuid?: string;

    constructor(message: string, currentGuid?: string) {
        super(message);
        this.name = "LoginMessageSupersededError";
        this.currentGuid = currentGuid;
    }
}

/**
 * GET the supplier's current in-window login message plus the caller's acknowledgment state.
 */
export const getActiveLoginMessage = async (
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IActiveLoginMessage | null> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.GetActiveLoginMessage });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    const payload: IOnGetActiveLoginMessageDTO = await response.json();

    return payload.loginMessage ?? null;
};

/**
 * POST the user's "I understand" for the message row they saw. Idempotent server-side.
 */
export const acknowledgeLoginMessage = async (
    loginMessageGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnAcknowledgeLoginMessageDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.AcknowledgeLoginMessage });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });
    const body: IAcknowledgeLoginMessageDTO = { loginMessageGuid };

    const response = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });

    if (response.status === 409) {
        const errorData = await response.json().catch(() => ({}));
        throw new LoginMessageSupersededError(errorData.message || "Login message has changed", errorData.current_guid);
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

export const hasLoginMessageContent = (detail?: ILogin_Message_Detail | null): boolean => {
    return !!(detail?.title?.trim().length || detail?.detail?.trim().length);
};

/**
 * Whether the message's display window contains `now`. A missing start has no lower bound and a
 * blank/missing end is open-ended (a message with only a start date displays).
 */
export const isActiveLoginMessage = (detail?: ILogin_Message_Detail | null, now: Moment = moment()): boolean => {
    if (!detail) {
        return false;
    }

    const beginsAt = detail.begins_at ? moment(detail.begins_at) : null;
    const expiresAt = detail.expires_at ? moment(detail.expires_at) : null;

    if (beginsAt && beginsAt.isValid() && now.isBefore(beginsAt)) {
        return false;
    }

    if (expiresAt && expiresAt.isValid() && now.isAfter(expiresAt)) {
        return false;
    }

    return true;
};

/**
 * Whether the inline banner should render. Messages that do not require acknowledgment always
 * do; required ones are gated by the blocking modal first and only appear as a banner (collapsed)
 * once the user has acknowledged them, so they can be read again.
 */
export const shouldShowLoginMessageBanner = (detail?: ILogin_Message_Detail | null, acknowledged: boolean = false): boolean => {
    return hasLoginMessageContent(detail) && (detail?.requires_acknowledgment !== true || acknowledged);
};

/**
 * OR-343: a message saved without a start date never displayed. Default a blank begins_at to the
 * start of today so it shows immediately after save (the server applies the same default).
 */
export const withDefaultBeginsAt = (
    detail: ILogin_Message_Detail | null | undefined,
    now: Moment = moment()
): ILogin_Message_Detail | null => {
    if (!detail) {
        return null;
    }

    if (!hasLoginMessageContent(detail) || (detail.begins_at && detail.begins_at.trim().length)) {
        return detail;
    }

    return { ...detail, begins_at: now.clone().startOf("day").format(LOGIN_MESSAGE_DATE_FORMAT) };
};

const LoginMessageService = {
    getActiveLoginMessage,
    acknowledgeLoginMessage,
    hasLoginMessageContent,
    isActiveLoginMessage,
    shouldShowLoginMessageBanner,
    withDefaultBeginsAt,
};

export default LoginMessageService;
